/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { View, Text } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import { Seo, Footer } from '@components/systems'
import { mono, monoFont, monoShared } from '@components/systems/monolith/monolith.tokens'
import { checkPickedFile } from '@components/systems/monolith/avatar/imageGuard'
import { contactTopicList, contactTemplate } from '@src/configs/contact.config'

import {
	useAppDispatch,
	useAppSelector,
	inquiryActions,
	InquiryStateType,
} from '@redux/index'
import { produce } from 'immer'
import { deepPurple } from '@mui/material/colors'

/**
 * Contact — Monolith(2안).
 *
 * 목업: claudeFiles/judahsWeb/homepage-mockups/2안-monolith/contact.html
 * 폼 상태·전송·결과 모달은 기존 Contact.page.tsx 의 redux(inquiry) 흐름을 그대로 쓴다.
 * 목업은 "Topic" 을 칩으로 그렸지만, 2026-09-09 구 페이지의 Category 드롭다운(5종)으로 되돌렸다.
 * 어느 쪽이든 categ 값은 그대로다.
 *
 * 2026-09-15 — 이미지 첨부(최대 5장). multipart `files` 로 보내고, 서버가 webp 로 재인코딩해 S3 에 두고
 * `INQUIRY_IMAGE` 에 링크만 남긴다(judahsWeb/migrate_inquiry_image.sql).
 */

/**
 * Topic 목록·Message 템플릿은 `configs/contact.config.ts` 에 있다 — Works with 페이지가 같은 토픽으로 링크한다.
 * 구 페이지의 Category 5종에 2026-09-09 세 항목, 2026-09-15 Works with(장비 추가·정정)를 더했다.
 * `/contact?topic={value}` 로 들어오면 그 토픽이 골라지고 템플릿이 채워진다.
 */

/** 첨부 이미지 — 서버 한도(`MAX_INQUIRY_IMAGES` · 8MB)와 같은 선 */
const MAX_IMAGES = 5
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif'
/** INQUIRY.MESSAGE varchar(1000) */
const MAX_MESSAGE = 1000

interface PickedImageType {
	file: File
	previewUrl: string
}

/** Lucide `plus` (ISC) 원본 그대로 — 24 viewBox. strokeWidth 만 1. */
const PlusIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={22}
		height={22}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M5 12h14" />
		<path d="M12 5v14" />
	</svg>
)

/** Lucide `x` (ISC) 원본 그대로 — 24 viewBox. */
const XIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={14}
		height={14}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.5}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M18 6 6 18" />
		<path d="m6 6 12 12" />
	</svg>
)

/** Lucide `chevron-down` (ISC) 원본 그대로 — 24 viewBox, path "m6 9 6 6 6-6". strokeWidth 만 1. */
const ChevronDown = () => (
	<svg
		className="mnSelectChevron"
		xmlns="http://www.w3.org/2000/svg"
		width={18}
		height={18}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m6 9 6 6 6-6" />
	</svg>
)

const MonolithContactPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const inquiry: InquiryStateType['insertInquiry'] = useAppSelector(
		state => state.inquiry.insertInquiry,
	)
	const errorMessages: InquiryStateType['inquiryErrorMessage'] = useAppSelector(
		state => state.inquiry.inquiryErrorMessage,
	)
	const showModal: boolean = useAppSelector(state => state.inquiry.showModal)
	const modalInfo: ModalInfoType = useAppSelector(state => state.inquiry.modalInfo)
	const isSending: boolean = useAppSelector(state => state.inquiry.isSendingInquiry)

	/*
		로그인 고객 — 이름·이메일을 계정 값으로 채우고 이메일은 잠근다. 문의는 이메일로 계정과 이어져
		My Inquiries 에 뜬다(INQUIRY_THREAD_PLAN §2). 다른 주소로 쓰면 거기 안 뜬다.
	*/
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const customerName: string = useAppSelector(state => state.customerAuth.customer.name)
	const customerEmail: string = useAppSelector(state => state.customerAuth.customer.email)
	const isAccountEmail: boolean = isLogedin && (customerEmail ?? '') !== ''

	const location = useLocation()
	const navigate = useNavigate()
	const fileInputRef = useRef<HTMLInputElement>(null)

	// 첨부 이미지는 File 이라 redux(세션 저장)에 두지 않는다 — 이 화면에만 산다.
	const [images, setImages] = useState<PickedImageType[]>([])
	const [imageError, setImageError] = useState('')
	const imagesRef = useRef<PickedImageType[]>([])
	imagesRef.current = images

	const update = (patch: Partial<InquiryStateType['insertInquiry']>) => {
		const newInquiry = produce(inquiry, draft => {
			Object.assign(draft, patch)
		})
		reduxDispatch(inquiryActions.INSERT_INQUIRY(newInquiry))
	}

	/**
	 * 토픽 고르기 — 셀렉트와 `?topic=` 이 같이 쓴다.
	 * Message 는 비어 있거나 템플릿 그대로일 때만 새 토픽의 템플릿으로 바꾼다. 사용자가 쓴 글은 덮지 않는다.
	 */
	const selectTopic = (categ: string) => {
		const isUntouched =
			inquiry.message.trim() === '' ||
			Object.values(contactTemplate).includes(inquiry.message)

		update(isUntouched ? { categ, message: contactTemplate[categ] ?? '' } : { categ })
	}

	// `/contact?topic=worksWithDevice` 처럼 들어오면 그 토픽을 고른다.
	useEffect(() => {
		const topic = new URLSearchParams(location.search).get('topic')
		if (topic !== null && contactTopicList.some(aTopic => aTopic.value === topic)) {
			selectTopic(topic)
		}
		// 주소가 바뀔 때만 — 입력할 때마다 다시 고르면 사용자가 바꾼 토픽이 되돌아간다
	}, [location.search])

	// 로그인 고객이면 계정 이메일로 맞춘다. 보낸 뒤 폼을 비워도(email '') 다시 채워진다.
	useEffect(() => {
		if (!isAccountEmail || inquiry.email === customerEmail) return
		update({ email: customerEmail, name: inquiry.name || customerName || '' })
		// update 는 매 렌더 새로 만들어진다 — 값이 바뀔 때만 돈다
	}, [isAccountEmail, customerEmail, inquiry.email])

	// 전송 중에 새로고침하면 세션 저장된 true 가 남는다 — 들어올 때 푼다.
	useEffect(() => {
		reduxDispatch(inquiryActions.IS_SENDING_INQUIRY(false))
	}, [reduxDispatch])

	// 떠날 때 미리보기 URL 을 돌려준다
	useEffect(
		() => () => {
			imagesRef.current.forEach(anImage => URL.revokeObjectURL(anImage.previewUrl))
		},
		[],
	)

	const onPickImages = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = Array.from(e.target.files ?? [])
		e.target.value = ''
		setImageError('')

		const room = MAX_IMAGES - images.length
		if (picked.length > room) {
			setImageError(`Up to ${MAX_IMAGES} images.`)
		}

		const accepted: PickedImageType[] = []
		for (const aFile of picked.slice(0, Math.max(room, 0))) {
			// 브라우저 사전 검사는 UX 일 뿐이다 — 검사가 실패하면 서버에 맡긴다(imageGuard 주석)
			const checkResult = await checkPickedFile(aFile).catch(() => ({
				ok: true as const,
			}))
			if (!checkResult.ok) {
				setImageError(`${aFile.name}: ${checkResult.message}`)
				continue
			}
			accepted.push({ file: aFile, previewUrl: URL.createObjectURL(aFile) })
		}

		if (accepted.length > 0) {
			setImages(prev => [...prev, ...accepted])
		}
	}

	const onRemoveImage = (previewUrl: string) => {
		URL.revokeObjectURL(previewUrl)
		setImages(prev => prev.filter(anImage => anImage.previewUrl !== previewUrl))
		setImageError('')
	}

	const clearImages = () => {
		images.forEach(anImage => URL.revokeObjectURL(anImage.previewUrl))
		setImages([])
		setImageError('')
	}

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (isSending) return
		reduxDispatch(
			inquiryActions.SEND_INQUIRY({ files: images.map(anImage => anImage.file) }),
		)
	}

	return (
		<View css={style}>
			<Seo />

			<View className="mnWrap">
				<View className="mnContactHeader">
					<h1 className="mnContactTitle">Contact</h1>
					<p className="mnContactLede">
						Products, orders, repairs, dealers. Write to us.
					</p>
				</View>

				<form className="mnForm" onSubmit={onSubmit} noValidate>
					<View className="mnFormRow2">
						<View className="mnField">
							<label className="mnLabel" htmlFor="mnName">
								Name
							</label>
							<input
								id="mnName"
								type="text"
								className={`mnInput ${errorMessages.name !== '' ? 'error' : ''}`}
								value={inquiry.name}
								onChange={e => update({ name: e.target.value })}
								autoComplete="name"
							/>
							{errorMessages.name !== '' && (
								<span className="mnError">{errorMessages.name}</span>
							)}
						</View>
						<View className="mnField">
							<label className="mnLabel" htmlFor="mnEmail">
								Email
							</label>
							<input
								id="mnEmail"
								type="email"
								className={`mnInput ${errorMessages.email !== '' ? 'error' : ''}`}
								value={inquiry.email}
								onChange={e => update({ email: e.target.value })}
								readOnly={isAccountEmail}
								autoComplete="email"
							/>
							{errorMessages.email !== '' && (
								<span className="mnError">{errorMessages.email}</span>
							)}
							{isAccountEmail && errorMessages.email === '' && (
								<span className="mnHint">Replies also appear in My Inquiries.</span>
							)}
						</View>
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnTopic">
							Topic
						</label>
						<View className="mnSelectWrap">
							<select
								id="mnTopic"
								name="categ"
								className={`mnInput mnSelect ${
									errorMessages.categ !== '' ? 'error' : ''
								}`}
								value={inquiry.categ}
								data-empty={inquiry.categ === '' ? 'true' : 'false'}
								onChange={e => selectTopic(e.target.value)}
							>
								<option value="" disabled>
									Choose a topic
								</option>
								{contactTopicList.map(aTopic => (
									<option key={aTopic.value} value={aTopic.value}>
										{aTopic.label}
									</option>
								))}
							</select>
							<ChevronDown />
						</View>
						{errorMessages.categ !== '' && (
							<span className="mnError">Choose a topic.</span>
						)}
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnMessage">
							Message
						</label>
						<textarea
							id="mnMessage"
							className={`mnInput mnTextarea ${
								errorMessages.message !== '' ? 'error' : ''
							}`}
							value={inquiry.message}
							onChange={e => update({ message: e.target.value })}
							rows={6}
							maxLength={MAX_MESSAGE}
						/>
						<View className="mnFieldFoot">
							{errorMessages.message !== '' ? (
								<span className="mnError">Write a message.</span>
							) : (
								<span />
							)}
							<span className="mnCounter">
								{inquiry.message.length} / {MAX_MESSAGE}
							</span>
						</View>
					</View>

					<View className="mnField">
						<span className="mnLabel" id="mnImagesLabel">
							Images
						</span>
						<ul className="mnAttach" aria-labelledby="mnImagesLabel">
							{images.map((anImage, index) => (
								<li key={anImage.previewUrl} className="mnAttachItem">
									<img src={anImage.previewUrl} alt={`Attached image ${index + 1}`} />
									<button
										type="button"
										className="mnAttachRemove"
										onClick={() => onRemoveImage(anImage.previewUrl)}
										aria-label={`Remove image ${index + 1}`}
										disabled={isSending}
									>
										<XIcon />
									</button>
								</li>
							))}
							{images.length < MAX_IMAGES && (
								<li>
									<button
										type="button"
										className="mnAttachAdd"
										onClick={() => fileInputRef.current?.click()}
										aria-label="Add image"
										disabled={isSending}
									>
										<PlusIcon />
									</button>
								</li>
							)}
						</ul>
						<input
							ref={fileInputRef}
							type="file"
							accept={IMAGE_ACCEPT}
							multiple
							hidden
							onChange={e => {
								void onPickImages(e)
							}}
						/>
						{imageError !== '' ? (
							<span className="mnError">{imageError}</span>
						) : (
							<span className="mnHint">
								Up to {MAX_IMAGES}. JPEG, PNG, WebP or GIF, 8MB each.
							</span>
						)}
					</View>

					<View className="mnFormAction">
						<button type="submit" className="mnCta mnSend" disabled={isSending}>
							{isSending ? 'Sending' : 'Send'}
						</button>
					</View>
				</form>

				<View className="mnBand mnInfo">
					<View className="mnInfoItem">
						<span className="mnLabel">Email</span>
						<p>
							<a href="mailto:cs@judahs.io">cs@judahs.io</a>
						</p>
					</View>
					<View className="mnInfoItem">
						<span className="mnLabel">Announcements</span>
						<p>New releases go out by email first.</p>
					</View>
					<View className="mnInfoItem">
						<span className="mnLabel">Editor</span>
						<p>Judahs Studio, for macOS and Windows.</p>
					</View>
				</View>
			</View>

			<Footer />

			<Modal
				target={modalInfo.target ?? ''}
				title={modalInfo.title}
				show={showModal}
				onCloseModal={() => {
					reduxDispatch(inquiryActions.SHOW_MODAL(false))

					if (modalInfo.target === 'submitSuccess') {
						clearImages()
						reduxDispatch(
							inquiryActions.INSERT_INQUIRY({
								name: '',
								email: '',
								categ: '',
								message: '',
							}),
						)
					}
				}}
				showSubmit={false}
				closeStatus="inherit"
				closeTitle={'Close'}
				submitTitle="Get Code"
				submitIconColor={deepPurple['A400']}
				isTitleDanger={['submitError'].includes(modalInfo.target)}
			>
				{modalInfo.target === 'submitSuccess' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">Your message has been sent.</Text>
						<Text category="p2">
							We reply from cs@judahs.io. Check your spam folder if nothing arrives.
						</Text>
						{isAccountEmail && (
							<button
								type="button"
								// 모달은 포털이라 페이지 스타일이 닿지 않는다 — 인라인으로
								css={{
									marginTop: 12,
									padding: 0,
									border: 0,
									borderBottom: `1px solid ${mono.ink}`,
									background: 'transparent',
									fontFamily: monoFont.sans,
									fontSize: 14,
									color: mono.ink,
									cursor: 'pointer',
								}}
								onClick={() => {
									reduxDispatch(inquiryActions.SHOW_MODAL(false))
									navigate('/customer/inquiry')
								}}
							>
								It is in My Inquiries.
							</button>
						)}
					</View>
				) : modalInfo.target === 'submitError' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">The message could not be sent.</Text>
						<Text category="p2">
							Please try again later, or write directly to cs@judahs.io.
						</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

export default MonolithContactPage

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	'.mnContactHeader': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: 'clamp(64px, 8.3vw, 120px) 0 0',

		'.mnContactTitle': {
			fontSize: 'clamp(56px, 7.6vw, 110px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnContactLede': {
			marginTop: 36,
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
		},
	},

	'.mnForm': {
		width: '100%',
		maxWidth: 640,
		margin: '0 auto',
		padding: 'clamp(56px, 6.25vw, 90px) 0 clamp(64px, 8.3vw, 120px)',
		display: 'flex',
		flexDirection: 'column',
		gap: 44,
	},

	'.mnFormRow2': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 40,

		'@media (max-width: 600px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.mnField': {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
	},

	'.mnLabel': {
		fontSize: 12,
		letterSpacing: '.2em',
		textTransform: 'uppercase',
		color: mono.faint,
		fontWeight: 500,
	},

	'.mnInput': {
		width: '100%',
		height: 46,
		padding: 0,
		border: 0,
		borderBottom: `1px solid ${mono.lineInput}`,
		borderRadius: 0,
		background: 'transparent',
		fontFamily: monoFont.sans,
		fontSize: 16,
		fontWeight: 300,
		color: mono.ink,
		outline: 'none',
		boxSizing: 'border-box',

		'&:focus': {
			borderBottomColor: mono.ink,
		},
		'&.error': {
			borderBottomColor: mono.error,
		},
		'&.mnTextarea': {
			height: 180,
			resize: 'vertical',
			paddingTop: 10,
			lineHeight: 1.6,
		},
	},

	'.mnError': {
		fontSize: 12,
		color: mono.error,
		fontWeight: 400,
	},

	// 로그인 고객의 이메일 — 계정 값으로 잠겨 있다
	'.mnInput[readonly]': { color: mono.dim, cursor: 'default' },

	'.mnHint': {
		fontSize: 12,
		color: mono.faint,
		fontWeight: 400,
	},

	'.mnFieldFoot': {
		display: 'flex',
		justifyContent: 'space-between',
		gap: 16,

		'.mnCounter': {
			fontSize: 12,
			color: mono.faint,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	// 첨부 이미지 — 정사각 타일. 헤어라인 + 2px 모서리(DESIGN_SYSTEM §5), 추가 타일도 같은 규격.
	'.mnAttach': {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		display: 'flex',
		flexWrap: 'wrap',
		gap: 10,

		'.mnAttachItem': {
			position: 'relative',
			width: 88,
			height: 88,
			border: `1px solid ${mono.lineSoft}`,
			borderRadius: 2,
			overflow: 'hidden',

			img: {
				width: '100%',
				height: '100%',
				objectFit: 'cover',
				display: 'block',
			},
		},

		'.mnAttachRemove': {
			position: 'absolute',
			top: 4,
			right: 4,
			width: 24,
			height: 24,
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			padding: 0,
			border: 0,
			borderRadius: 2,
			background: 'rgba(255, 255, 255, 0.9)',
			color: mono.ink,
			cursor: 'pointer',
		},

		'.mnAttachAdd': {
			width: 88,
			height: 88,
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			padding: 0,
			border: `1px solid ${mono.lineSoft}`,
			borderRadius: 2,
			background: 'transparent',
			color: mono.faint,
			cursor: 'pointer',
			transition: 'border-color 200ms ease-out, color 200ms ease-out',

			'&:hover': {
				borderColor: mono.ink,
				color: mono.ink,
			},
			'&:disabled': {
				cursor: 'default',
				opacity: 0.5,
			},
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
			},
		},

		'button:focus-visible': {
			outline: `1px solid ${mono.ink}`,
			outlineOffset: 3,
		},
	},

	// Topic 셀렉트 — 구 페이지의 Category 드롭다운을 되살리되 MUI Select 외형은 쓰지 않는다
	// (DESIGN_SYSTEM §5). 네이티브 select 를 밑줄 입력과 같은 규격으로 눕히고 셰브론만 얹었다.
	'.mnSelectWrap': {
		position: 'relative',
		width: '100%',

		'.mnSelect': {
			appearance: 'none',
			WebkitAppearance: 'none',
			paddingRight: 28,
			cursor: 'pointer',
			color: mono.ink,

			'&[data-empty="true"]': {
				color: mono.faint,
			},
		},

		'.mnSelectChevron': {
			position: 'absolute',
			right: 2,
			top: '50%',
			transform: 'translateY(-50%)',
			color: mono.faint,
			pointerEvents: 'none',
		},
	},

	'.mnFormAction': {
		display: 'flex',
		justifyContent: 'center',
		marginTop: 12,

		'.mnSend': {
			padding: '0 60px',

			'&:disabled': {
				cursor: 'default',
				opacity: 0.6,
			},
		},
	},

	'.mnInfo': {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: 64,
		textAlign: 'center',
		padding: 'clamp(56px, 6.25vw, 90px) 0',

		'.mnInfoItem': {
			display: 'flex',
			flexDirection: 'column',
			gap: 14,

			p: {
				fontSize: 16,
				fontWeight: 300,
				color: mono.ink,
			},
			a: {
				color: mono.ink,
				borderBottom: `1px solid ${mono.lineSoft}`,
			},
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 40,
		},
	},
})
